import { IPlayerProps, ISceneBlock, ISendEvents, Id } from '../../../interfaces';
import { SceneMimeTypes } from '../scene-blocks/mime-constants';
import * as _ from 'lodash';

export interface Slide {
    id: number;
    title: string;
    privateName: string;
    sceneId?: string;
}

export interface SliderCreationSettings {
    width: number;
    height: number;
    name: string;
}

export type NavigationButtonType = "first" | "prev" | "next" | "last";
export type NavigationButtonSize = 1 | 2 | 3 | 4 | 5;

export class TouchLib {
    
    static createSlide(
        slideIndex: number, 
        title: string, 
        privateName: string,
        sceneId?: string
    ): Slide {
        return {
            id: slideIndex,
            title,
            privateName,
            sceneId
        };
    }

    static getSliderDimensions(sliderScene: IPlayerProps): { width: string; height: string } {
        if (!sliderScene) return { width: '1920', height: '1080' };
        
        return {
            width: sliderScene.Player.Data[0].Layout[0].$.width,
            height: sliderScene.Player.Data[0].Layout[0].$.height
        };
    }

    static getSliderBasicInfo(sliderScene: IPlayerProps): { 
        sceneId: string; 
        name: string; 
        width: string; 
        height: string; 
    } {
        if (!sliderScene) return { 
            sceneId: '', 
            name: '', 
            width: '1920', 
            height: '1080' 
        };

        const dimensions = this.getSliderDimensions(sliderScene);
        
        return {
            sceneId: sliderScene.Player.$.id,
            name: sliderScene.Player.$.label,
            width: dimensions.width,
            height: dimensions.height
        };
    }

    static extractSlidesFromSlider(sliderScene: IPlayerProps): Slide[] {
        if (!sliderScene) return [];

        const players = sliderScene.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
        if (!players || players.length === 0) return [];

        const componentName = this.getComponentName(sliderScene);
        const componentPlayer = players.find(p => p.Data?.[0]?.[componentName]);
        if (!componentPlayer) return [];

        const pages = componentPlayer.Data[0][componentName][0].Page;
        if (!pages || pages.length === 0) return [];

        return pages.map((p, ind) => {
            const sceneId = p?.Player?.[0]?.$?.src;

            // Use the real scene label if available, otherwise fallback
            const sceneTitle = `slide ${ind + 1}`

            return this.createSlide(ind, sceneTitle, p.$.page, sceneId);
        }).filter((slide) => !!slide.sceneId);
    }

    static getPlaylistId(sliderScene: IPlayerProps): string | null {
        if (!sliderScene) return null;

        try {
            const players = sliderScene.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
            if (!players || players.length === 0) return null;
            const componentName = this.getComponentName(sliderScene);
            const componentPlayer = players.find(p => p.Data?.[0]?.[componentName]);
            return componentPlayer?.$.id || null;
        } catch (error) {
            console.warn('Could not extract playlist ID:', error);
            return null;
        }
    }

    static getCurrentPages(sliderScene: IPlayerProps): any[] {
        if (!sliderScene) return [];

        try {
            const players = sliderScene.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
            if (!players || players.length === 0) return [];
            const componentName = this.getComponentName(sliderScene);
            const componentPlayer = players.find(p => p.Data?.[0]?.[componentName]);
            if (!componentPlayer) return [];

            return _.cloneDeep(componentPlayer.Data[0][componentName][0].Page || []);
        } catch (error) {
            console.warn('Could not extract pages:', error);
            return [];
        }
    }

    static createNewSlideScene(
        width: string,
        height: string,
        blockLabels: any,
        newSlideName: string,
        labelText?: string
    ): { scene: any; sceneId: string } {
        const subSceneId = _.uniqueId();
        const subScene: ISceneBlock = _.cloneDeep(blockLabels.BLOCKCODE_SCENE.template);
        const newSubScene = { Player: subScene };

        newSubScene.Player.$.id = subSceneId;
        newSubScene.Player.Data[0].Layout[0].$.width = width;
        newSubScene.Player.Data[0].Layout[0].$.height = height;
        newSubScene.Player.$.label = newSlideName;
        newSubScene.Player.$.mimeType = SceneMimeTypes.Touch;
        newSubScene.Player.$.private_child = true;
        if (newSubScene.Player.Data[0].Animation) {
            delete newSubScene.Player.Data[0].Animation;
        }

        // Add a default label to the slide scene
        const labelBlockData: ISceneBlock = _.cloneDeep(blockLabels.LABEL.template);
        labelBlockData.$.id = _.uniqueId("__");
        const layout = labelBlockData.Data["0"].Layout["0"]?.$;
        layout.width = `${+width / 2}`;
        layout.height = "50";
        layout.x = `${+width / 4}`;
        layout.y = `${+height / 3}`;
        const font = labelBlockData.Data["0"].Label["0"];
        font.Font["0"].$.fontSize = "50";
        font.Text = [labelText || "New Slide"];

        newSubScene.Player.Data[0].Scene[0].Players[0].Player = [labelBlockData];

        return {
            scene: newSubScene,
            sceneId: subSceneId
        };
    }

    static createPlaylistPage(slideSceneLabel: string, slideSceneId: string): any {
        return {
            $: {
                page: slideSceneLabel,
                type: "scene",
            },
            Player: [{
                $: {
                    src: slideSceneId,
                },
            }],
        };
    }

    static createSelectedSliderPayload(
        sceneId: string,
        sceneTitle: string,
        firstChildSceneId?: string | null
    ): any {
        return {
            selectedTouch: {
                sceneId,
                SceneTitle: sceneTitle,
                ScenePlaylistId: firstChildSceneId || null
            }
        };
    }

    /**
     * Handles smooth slide transition with masking effect to prevent jerkiness
     * @param slideSceneId - The scene ID of the slide to transition to
     * @param setTransitioningSlideId - Function to set the transitioning slide ID
     * @param triggerChangeDetection - Function to trigger Angular change detection
     * @returns Promise that resolves when transition is complete
     */
    static async handleSmoothSlideTransition(
        slideSceneId: string,
        setTransitioningSlideId: (id: string | null) => void,
        triggerChangeDetection: () => void
    ): Promise<void> {
        // Capture the current slide dimensions before hiding it
        const slideElement = document.querySelector(`[data-slide-id="${slideSceneId}"] .slide-smooth-wrapper`);
        if (slideElement) {
            const rect = slideElement.getBoundingClientRect();
            // Store dimensions on the element for CSS to use
            (slideElement as HTMLElement).style.setProperty('--captured-width', `${rect.width}px`);
            (slideElement as HTMLElement).style.setProperty('--captured-height', `${rect.height}px`);
        }
        
        // Start masking transition - hide the slide during resize
        setTransitioningSlideId(slideSceneId);
        triggerChangeDetection();
        
        // Return a promise that resolves when the transition is complete
        return new Promise((resolve) => {
            setTimeout(() => {
                setTransitioningSlideId(null);
                triggerChangeDetection();
                resolve();
            }, 150); // Optimized to 150ms for minimal but smooth transition
        });
    }

    /**
     * Get duration for playlist
     * @param sliderScene
     * @returns
     */
    static getDuration(sliderScene: IPlayerProps) {
        if (!sliderScene) return '0';
        const componentName = this.getComponentName(sliderScene);
        const players = sliderScene.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
        const componentPlayer = players?.find(p => p.Data?.[0]?.[componentName]);

        return componentPlayer?.Data?.[0]?.[componentName]?.[0]?.$?.duration || '0';
    }

    /**
     * Get duration for playlist
     * @param sliderScene
     * @returns
     */
    static getPagesLength(sliderScene: IPlayerProps) {
        if (!sliderScene) return 0;
        const componentName = this.getComponentName(sliderScene);
        const players = sliderScene.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
        const componentPlayer = players?.find(p => p.Data?.[0]?.[componentName]);

        return componentPlayer?.Data?.[0]?.[componentName]?.[0]?.Page?.length;
    }

    /**
     * check if the slider is collection or playlist
     * @param sliderScene
     * @returns
     */
    static isCollection(sliderScene: IPlayerProps) {
        if (!sliderScene) return true;
        const players = sliderScene.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
        if (!players || players.length === 0) return true;

        const componentPlayer = players.find(p => p.Data?.[0]?.Collection || p.Data?.[0]?.Playlist);
        if (!componentPlayer) return true;

        return !!componentPlayer.Data[0].Collection;
    }

    /**
     * Get slider component name
     * @param sliderScene
     * @returns: 'Collection' | 'Playlist'
     */
    static getComponentName(sliderScene: IPlayerProps): 'Collection' | 'Playlist' {
        return this.isCollection(sliderScene) ? 'Collection': 'Playlist';
    }

    static getFirstSlideSceneId(sliderScene: IPlayerProps) {
        let firstChildSceneId: string | null = null;
        try {
            const componentName = this.getComponentName(sliderScene);
            const players = sliderScene.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
            const componentPlayer = players?.find(p => p.Data?.[0]?.[componentName]);

            const firstPage = componentPlayer?.Data?.[0]?.[componentName]?.[0]?.Page?.[0];
            if (firstPage && firstPage.$.type === "scene" && firstPage.Player?.[0]?.$.src) {
                firstChildSceneId = firstPage.Player[0].$.src;
            }
        } catch (error) {
            console.warn("Could not extract first child scene ID:", error);
        }
        return firstChildSceneId;
    }

    /**
     * Find the parent Touch scene (wrapper) that contains a specific child scene ID
     * @param childSceneId - The scene ID to search for
     * @param allScenes - Array of all scenes to search through
     * @returns The parent Touch scene if found, null otherwise
     */
    static findParentTouchScene(childSceneId: string, allScenes: IPlayerProps[]): IPlayerProps | null {
        if (!childSceneId || !allScenes) return null;

        for (const scene of allScenes) {
            if (scene.Player?.$.mimeType !== SceneMimeTypes.Touch) continue;
            if (!scene.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player) continue;

            try {
                const componentName = this.getComponentName(scene);
                const players = scene.Player.Data[0].Scene[0].Players[0].Player;
                const componentPlayer = players.find(p => p.Data?.[0]?.[componentName]);
                if (!componentPlayer) continue;

                const pages = componentPlayer.Data[0][componentName][0].Page;

                if (pages && pages.length > 0) {
                    const foundChild = pages.find((page) => {
                        return page?.Player?.[0]?.$?.src === childSceneId;
                    });

                    if (foundChild) {
                        return scene;
                    }
                }
            } catch (error) {
                continue;
            }
        }

        return null;
    }

    static uniqueName(prefix = null) {
        if (!prefix) prefix = 'slide';
        return `${prefix}_${Math.random().toString(36).substring(2, 6)}`;
    }

    static touchEventName(command) {
        if (command === 'selectPage') {
            return `touch_${Math.random().toString(36).substring(2, 6)}`;
        }
        return `touch_${command}`;
    }

    
    static getSendEventTemplate(): ISendEvents {
        return {
            $: {
                from: "",
                command: "",
            },
            Params: [
                {
                    Page: [
                        {
                            $: {
                                name: "",
                                page: "",
                                type: "",
                            },
                            Player: [
                                {
                                    $: {},
                                },
                            ],
                        },
                    ],
                },
            ],
        };
    }
}
